// Flowhub Storefront constants & curated product catalog for the Shop page.
// Iframe embed is blocked by Flowhub CSP (frame-ancestors 'self' *.flowhub.com),
// so we render a custom catalog that deep-links to the live Flowhub PDPs and
// category search URLs. Product entries below are curated from the live menu
// and should be refreshed periodically as inventory rotates.

export const FLOWHUB_BASE = "https://districtwellnesscenter.dispensary.shop";
export const FLOWHUB_MENU_URL = `${FLOWHUB_BASE}/med/menu`;

// Brand images extracted from the live Flowhub menu.
// Alt Sol uses a shared brand fallback for all SKUs without product photos.
const ALT_SOL_IMG =
  "https://origin.dispensary.shop/cdn-cgi/image/quality=80,format=webp/https://storage.googleapis.com/maui-prod-hyper-media-images/global_raw_brand_images_67e0c485-1943-483e-a036-0cc4444cacda_43448df0-b7d9-45ce-8090-dd9b22217ef0_mobile.png";

// Category metadata: icon name (lucide-react), accent tailwind classes,
// and the direct Flowhub category URL.
export const FLOWHUB_CATEGORIES = [
  {
    name: "Flower",
    description: "Premium hand-picked buds",
    url: `${FLOWHUB_BASE}/med/search?category=Flower&title=Flower`,
    icon: "Leaf",
    accent: {
      text: "text-emerald-400",
      bg: "bg-emerald-500/10",
      ring: "group-hover:border-emerald-400/40",
      glow: "from-emerald-500/30 via-emerald-500/10",
    },
  },
  {
    name: "Joints & Prerolls",
    description: "Ready-to-light singles & packs",
    url: `${FLOWHUB_BASE}/med/search?category=Joints&title=Joints`,
    icon: "Flame",
    accent: {
      text: "text-amber-400",
      bg: "bg-amber-500/10",
      ring: "group-hover:border-amber-400/40",
      glow: "from-amber-500/30 via-amber-500/10",
    },
  },
  {
    name: "Infused Flower",
    description: "Flower enhanced with concentrate",
    url: `${FLOWHUB_BASE}/med/search?category=Infused+Flower&title=Infused%20Flower`,
    icon: "Sparkles",
    accent: {
      text: "text-fuchsia-400",
      bg: "bg-fuchsia-500/10",
      ring: "group-hover:border-fuchsia-400/40",
      glow: "from-fuchsia-500/30 via-fuchsia-500/10",
    },
  },
  {
    name: "Cartridges & Vapes",
    description: "510-thread carts & disposables",
    url: `${FLOWHUB_BASE}/med/search?category=Cartridges&title=Cartridges`,
    icon: "Battery",
    accent: {
      text: "text-sky-400",
      bg: "bg-sky-500/10",
      ring: "group-hover:border-sky-400/40",
      glow: "from-sky-500/30 via-sky-500/10",
    },
  },
  {
    name: "Deals",
    description: "Patient discounts of the week",
    url: `${FLOWHUB_BASE}/med/deals?title=Deals`,
    icon: "BadgePercent",
    accent: {
      text: "text-rose-400",
      bg: "bg-rose-500/10",
      ring: "group-hover:border-rose-400/40",
      glow: "from-rose-500/30 via-rose-500/10",
    },
  },
  {
    name: "All Products",
    description: "Browse the entire menu",
    url: `${FLOWHUB_BASE}/med/search?title=All%20Products`,
    icon: "LayoutGrid",
    accent: {
      text: "text-brand",
      bg: "bg-brand/10",
      ring: "group-hover:border-brand/50",
      glow: "from-brand/30 via-brand/10",
    },
  },
];

// Species → gradient palette used as artistic fallback when Flowhub has no
// product image (most SKUs currently use a placeholder).
const SPECIES_GRADIENT = {
  "Sativa-dom": "from-amber-500/40 via-orange-500/20 to-transparent",
  Sativa: "from-amber-500/40 via-orange-500/20 to-transparent",
  "Indica-dom": "from-fuchsia-500/40 via-purple-600/20 to-transparent",
  Indica: "from-fuchsia-500/40 via-purple-600/20 to-transparent",
  "50/50": "from-emerald-500/40 via-teal-500/20 to-transparent",
  "Hybrid 50/50": "from-emerald-500/40 via-teal-500/20 to-transparent",
  Hybrid: "from-emerald-500/40 via-teal-500/20 to-transparent",
};

export const gradientFor = (species) =>
  SPECIES_GRADIENT[species] ||
  "from-brand/40 via-brand/15 to-transparent";

// Curated bestsellers — synced from the live Flowhub "Best Selling" carousel.
// Updated 2026-02. Each item deep-links directly into the live PDP.
export const FLOWHUB_BESTSELLERS = [
  {
    name: "North Star OG",
    brand: "Molecular Farms",
    category: "Flower",
    species: "Sativa-dom",
    price: "$49",
    size: "3.5g",
    image: null,
    url: `${FLOWHUB_BASE}/med/flower/pdp/north-star-og-3.5g/v/a1d3eee7-7bbf-46f4-b036-00f0e2aa7abf`,
  },
  {
    name: "Permanent Marker Supremes",
    brand: "Alt Sol",
    category: "Preroll",
    species: "50/50",
    price: "$16",
    size: "1g",
    image: ALT_SOL_IMG,
    url: `${FLOWHUB_BASE}/med/joints/pdp/permanent-marker-supremes-1g-preroll/v/ca0ccc5a-510d-4caf-8a78-81a9f3830cc9`,
  },
  {
    name: "Choof *69 Preroll",
    brand: "Alt Sol",
    category: "Preroll",
    species: "Indica-dom",
    price: "$13",
    size: "1g",
    image: ALT_SOL_IMG,
    url: `${FLOWHUB_BASE}/med/joints/pdp/choof-*69-preroll-1g/v/a5f333d7-c5ae-455f-83de-8141b9a8401b`,
  },
  {
    name: "Duct Tape Flower",
    brand: "Alt Sol",
    category: "Flower",
    species: "Indica-dom",
    price: "$50",
    size: "3.5g",
    image: ALT_SOL_IMG,
    url: `${FLOWHUB_BASE}/med/flower/pdp/duct-tape-flower-3.5g/v/477d6e1e-ea82-4a73-9fa6-407225f9406a`,
  },
  {
    name: "Blue Sharpies Flower",
    brand: "Alt Sol",
    category: "Flower",
    species: "50/50",
    price: "$45",
    size: "3.5g",
    image: ALT_SOL_IMG,
    url: `${FLOWHUB_BASE}/med/flower/pdp/blue-sharpies-flower-3.5g/v/7a7175d7-1bc5-4aa0-85b4-ac22bdc6f27a`,
  },
  {
    name: "Crushed Ice",
    brand: "Molecular Farms",
    category: "Flower",
    species: "50/50",
    price: "$49",
    size: "3.5g",
    image: null,
    url: `${FLOWHUB_BASE}/med/flower/pdp/crushed-ice-3.5g/v/088def26-f1cd-4907-a3ba-531f8ec46d8e`,
  },
  {
    name: "Gelato 33 Flower",
    brand: "Cornerstone Cultivation",
    category: "Flower",
    species: "Hybrid 50/50",
    price: "$45",
    size: "3.5g",
    image: null,
    url: `${FLOWHUB_BASE}/med/flower/pdp/gelato-33-flower-3.5g/v/2976792d-7314-4192-817f-daf74d197226`,
  },
  {
    name: "Kandy Flower",
    brand: "Cornerstone Cultivation",
    category: "Flower",
    species: "Indica-dom",
    price: "$45",
    size: "3.5g",
    image: null,
    url: `${FLOWHUB_BASE}/med/flower/pdp/kandy-flower-3.5g/v/e04701a2-cdbe-4c1a-bce3-519934067546`,
  },
  {
    name: "Marrion Berries",
    brand: "Molecular Farms",
    category: "Flower",
    species: "Hybrid 50/50",
    price: "$40",
    size: "3.5g",
    image: null,
    url: `${FLOWHUB_BASE}/med/flower/pdp/marrion-berries-3.5g/v/a794e409-b79f-4987-b7fe-4535f06395a4`,
  },
];
